/**
 * TreatRxAdmin.tsx — Admin → Integrations → TreatRx.
 *
 * Two tabs over the two legacy tables the e-prescribing iframe reads:
 *  - Authorization → aa_accounts_ayva (the per-account OAuth client)
 *  - Providers     → aa_ayva_doctors  (user ↔ TreatRx doctor links)
 *
 * Shell modeled on VibrantAdmin/QuestAdmin (PageHeader + Tabs + DataTable +
 * Modal + ConfirmDialog); the credentials card follows AiSettingsAdmin.
 *
 * Locked (plan 2026-10-10-001 U7: R8, R18): the stored client shows, the
 * secret still masked, with no Add/Update Credentials or editing hints; the
 * providers list has no Add Provider, row actions (Edit, Clear Authorization,
 * Delete) or add hint. A provider row already shows every field its dialog
 * holds, so rows open nothing. Nothing says why.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, KeyRound, UserCog, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Tabs } from '@/components/ui/Tabs';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn, DataTableAction } from '@/components/ui/DataTable';
import { Card, CardBody } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui';
import { FormField } from '@/components/ui/FormField';
import { Select } from '@/components/ui/Select';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { getErrorMessage } from '@/services/apiClient';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import * as adminSvc from '@/services/admin.service';
import {
  getTreatRxAuthorization,
  updateTreatRxAuthorization,
  listTreatRxProviders,
  createTreatRxProvider,
  updateTreatRxProvider,
  deleteTreatRxProvider,
  resetTreatRxProviderCredentials,
  type TreatRxProviderRow,
} from '@/services/treatrxAdmin.service';

/** TreatRx doctor ids round-trip through (int) server-side — no leading zeros. */
const DOCTOR_ID_PATTERN = /^[1-9]\d*$/;

// ─── Authorization tab ────────────────────────────────────────────────────────

function AuthorizationTab() {
  const locked = useAdminPageLevel() === 'locked';
  const toast = useToast();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['treatrx', 'authorization'],
    queryFn: getTreatRxAuthorization,
    staleTime: 30_000,
  });

  const [editing, setEditing] = useState(false);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);

  function startEditing() {
    setClientId(data?.credentials.clientId ?? '');
    setClientSecret('');
    setShowSecret(false);
    setEditing(true);
  }

  function cancelEditing() {
    setEditing(false);
    setClientId('');
    setClientSecret('');
    setShowSecret(false);
  }

  const saveMutation = useMutation({
    mutationFn: updateTreatRxAuthorization,
    onSuccess: (res) => {
      toast.success('TreatRx credentials saved.');
      qc.setQueryData(['treatrx', 'authorization'], res);
      cancelEditing();
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to save TreatRx credentials.')),
  });

  function handleSave() {
    if (locked) return;
    const trimmedId = clientId.trim();
    if (!trimmedId) {
      toast.error('A Client ID is required.');
      return;
    }
    // A brand-new client needs both halves; an existing one may keep its secret.
    if (!data?.credentials.hasSecret && !clientSecret.trim()) {
      toast.error('A Client Secret is required.');
      return;
    }

    const payload: { clientId: string; clientSecret?: string } = { clientId: trimmedId };
    if (clientSecret.trim()) payload.clientSecret = clientSecret.trim();
    saveMutation.mutate(payload);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    );
  }

  const configured = data?.configured ?? false;
  const hasSecret = data?.credentials.hasSecret ?? false;

  return (
    <div className="p-6 space-y-6 max-w-2xl">
      <Card>
        <CardBody>
          <div className="flex items-center gap-3 mb-4">
            <KeyRound className="h-5 w-5 text-violet-500" />
            <h3 className="text-base font-semibold text-slate-200">TreatRx OAuth Client</h3>
            {configured ? (
              <Badge variant="success">Configured</Badge>
            ) : (
              <Badge variant="neutral">Not configured</Badge>
            )}
          </div>

          <p className="text-sm text-slate-400 mb-5">
            The Client ID and Secret TreatRx issued for this practice. They authorize the
            prescription window on a patient's chart.
            {!locked && ' Once saved, the secret can be replaced but never read back.'}
          </p>

          {!locked && (data?.rowCount ?? 0) > 1 && (
            <div className="flex items-start gap-2 text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-5">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                This account has {data?.rowCount} credential records. Saving here updates all of
                them so they stay in step.
              </span>
            </div>
          )}

          {!editing && (
            <>
              {configured && (
                <div className="space-y-3 mb-5">
                  <div>
                    <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                      Client ID
                    </span>
                    <p className="text-sm text-slate-200 font-mono mt-0.5">
                      {data?.credentials.clientId}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                      Client Secret
                    </span>
                    <p className="text-sm text-slate-200 font-mono mt-0.5">
                      {data?.credentials.secretHint}
                    </p>
                  </div>
                </div>
              )}

              {!locked && (
                <Button onClick={startEditing}>
                  {configured ? 'Update Credentials' : 'Add Credentials'}
                </Button>
              )}
            </>
          )}

          {editing && !locked && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">
                  Client ID
                </label>
                <Input
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder="TreatRx Client ID"
                  className="font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">
                  Client Secret
                </label>
                <div className="relative">
                  <Input
                    type={showSecret ? 'text' : 'password'}
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    placeholder={hasSecret ? 'Leave blank to keep current' : 'Enter client secret'}
                    className="font-mono pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecret(!showSecret)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                  >
                    {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button onClick={handleSave} disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? 'Saving...' : 'Save Credentials'}
                </Button>
                <Button variant="ghost" onClick={cancelEditing} disabled={saveMutation.isPending}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

// ─── Provider modal ───────────────────────────────────────────────────────────

function ProviderModal({
  provider,
  users,
  takenUserIds,
  onClose,
  onSaved,
}: {
  provider?: TreatRxProviderRow | null;
  users: adminSvc.AdminUser[];
  takenUserIds: Set<number>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = provider != null;
  const [userId, setUserId] = useState<number | null>(provider?.userId || null);
  const [ayvaDoctorID, setAyvaDoctorID] = useState(provider?.ayvaDoctorID ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Active staff only, minus anyone already linked — the API rejects a second
  // link for the same user, since the patient page resolves a user's default
  // doctor by userID alone.
  const userOptions = useMemo(
    () =>
      users
        .filter((u) => u.active)
        .filter((u) => !takenUserIds.has(u.userId) || u.userId === provider?.userId)
        .map((u) => ({
          value: u.userId,
          label:
            `${u.lastName}, ${u.firstName}`.replace(/^, |, $/g, '').trim() || `User #${u.userId}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [users, takenUserIds, provider?.userId],
  );

  const doctorIdValid = DOCTOR_ID_PATTERN.test(ayvaDoctorID.trim());
  const canSave = doctorIdValid && !!userId;
  const willClearCredentials =
    isEdit && provider.authorized && ayvaDoctorID.trim() !== provider.ayvaDoctorID;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSave || !userId) return;

    setSaving(true);
    setError(null);
    try {
      const payload = { userId, ayvaDoctorID: ayvaDoctorID.trim() };
      if (isEdit) {
        await updateTreatRxProvider(provider.id, payload);
      } else {
        await createTreatRxProvider(payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to save provider. Please try again.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? 'Edit Provider' : 'Add Provider'}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving || !canSave} loading={saving}>
            {isEdit ? 'Save Changes' : 'Add Provider'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
            {error}
          </p>
        )}

        <FormField label="User" required>
          <Select<number>
            options={userOptions}
            value={userId}
            onChange={(v) => setUserId(v)}
            placeholder="Select a user…"
            searchable
          />
        </FormField>

        <FormField label="TreatRx Doctor ID" required>
          <Input
            value={ayvaDoctorID}
            onChange={(e) => setAyvaDoctorID(e.target.value.replace(/\D/g, ''))}
            placeholder="Enter numeric TreatRx doctor ID"
            inputMode="numeric"
            required
          />
        </FormField>

        {willClearCredentials && (
          <p className="flex items-start gap-2 text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              Changing the doctor ID clears this provider's TreatRx authorization. They will need
              to grant access again.
            </span>
          </p>
        )}
      </form>
    </Modal>
  );
}

// ─── Providers tab ────────────────────────────────────────────────────────────

function ProvidersTab({ showCreate, onCreated }: { showCreate: boolean; onCreated: () => void }) {
  const locked = useAdminPageLevel() === 'locked';
  const toast = useToast();
  const [providers, setProviders] = useState<TreatRxProviderRow[]>([]);
  const [users, setUsers] = useState<adminSvc.AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState<TreatRxProviderRow | null>(null);
  const [deleteItem, setDeleteItem] = useState<TreatRxProviderRow | null>(null);
  const [resetItem, setResetItem] = useState<TreatRxProviderRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rows, usrs] = await Promise.all([listTreatRxProviders(), adminSvc.listUsers()]);
      setProviders(rows);
      setUsers(usrs);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const takenUserIds = useMemo(() => new Set(providers.map((p) => p.userId)), [providers]);

  async function handleDelete() {
    if (!deleteItem) return;
    try {
      await deleteTreatRxProvider(deleteItem.id);
      toast.success('Provider removed.');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to remove provider.'));
    } finally {
      setDeleteItem(null);
      void load();
    }
  }

  async function handleReset() {
    if (!resetItem) return;
    try {
      await resetTreatRxProviderCredentials(resetItem.id);
      toast.success('TreatRx authorization cleared.');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to clear authorization.'));
    } finally {
      setResetItem(null);
      void load();
    }
  }

  const columns: DataTableColumn<TreatRxProviderRow>[] = [
    {
      key: 'providerName',
      header: 'Provider',
      sortable: true,
      render: (row) => (
        <span className="font-medium text-slate-900">
          {row.providerName || `User #${row.userId}`}
        </span>
      ),
    },
    {
      key: 'ayvaDoctorID',
      header: 'TreatRx Doctor ID',
      render: (row) => <span className="font-mono text-sm text-slate-700">{row.ayvaDoctorID}</span>,
    },
    {
      key: 'authorized',
      header: 'TreatRx Access',
      render: (row) =>
        row.authorized ? (
          <Badge variant="success">Authorized</Badge>
        ) : (
          <Badge variant="neutral">Not authorized</Badge>
        ),
    },
    {
      key: 'userActive',
      header: 'User',
      render: (row) => {
        if (row.orphanedUser) return <Badge variant="error">Missing</Badge>;
        return row.userActive ? (
          <Badge variant="success">Active</Badge>
        ) : (
          <Badge variant="warning">Inactive</Badge>
        );
      },
    },
  ];

  // Locked: no row actions (the row already shows every field the dialog holds).
  const rowActions: DataTableAction<TreatRxProviderRow>[] = locked
    ? []
    : [
        { label: 'Edit', onClick: (row) => setEditItem(row) },
        {
          label: 'Clear Authorization',
          onClick: (row) => setResetItem(row),
          hidden: (row) => !row.authorized,
        },
        { label: 'Delete', variant: 'danger', onClick: (row) => setDeleteItem(row) },
      ];

  return (
    <div className="p-6">
      <DataTable<TreatRxProviderRow>
        data={providers}
        columns={columns}
        keyField="id"
        loading={loading}
        rowActions={rowActions}
        emptyTitle="No providers found"
        emptyDescription={locked ? '' : "Link a user to their TreatRx doctor ID so they can prescribe from a patient's chart."}
      />

      {showCreate && !locked && (
        <ProviderModal
          users={users}
          takenUserIds={takenUserIds}
          onClose={onCreated}
          onSaved={load}
        />
      )}
      {editItem && (
        <ProviderModal
          provider={editItem}
          users={users}
          takenUserIds={takenUserIds}
          onClose={() => setEditItem(null)}
          onSaved={load}
        />
      )}

      <ConfirmDialog
        isOpen={deleteItem !== null}
        title="Remove Provider"
        message={
          deleteItem
            ? `Remove "${deleteItem.providerName || deleteItem.ayvaDoctorID}"?` +
              (deleteItem.authorized
                ? ' Their TreatRx authorization will be lost and cannot be restored — they would need to grant access again.'
                : '')
            : ''
        }
        confirmLabel="Remove"
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setDeleteItem(null)}
      />

      <ConfirmDialog
        isOpen={resetItem !== null}
        title="Clear TreatRx Authorization"
        message={
          resetItem
            ? `Clear the stored TreatRx authorization for "${resetItem.providerName || resetItem.ayvaDoctorID}"? The link stays in place, but they will need to grant access again before prescribing.`
            : ''
        }
        confirmLabel="Clear"
        variant="danger"
        onConfirm={handleReset}
        onClose={() => setResetItem(null)}
      />
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function TreatRxAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const { hasAdminAccess } = useAuth();
  // Credentials follow Admin Access, matching the AI and Google Calendar
  // pages; hide the tab rather than let the FE walk into the server's 403.
  const canManageCredentials = hasAdminAccess;

  const [activeTab, setActiveTab] = useState(canManageCredentials ? 'authorization' : 'providers');
  const [showCreate, setShowCreate] = useState(false);

  const tabs = [
    ...(canManageCredentials
      ? [
          {
            id: 'authorization',
            label: 'Authorization',
            icon: <KeyRound className="h-4 w-4" />,
            content: <AuthorizationTab />,
          },
        ]
      : []),
    {
      id: 'providers',
      label: 'Providers',
      icon: <UserCog className="h-4 w-4" />,
      content: (
        <ProvidersTab
          showCreate={showCreate && activeTab === 'providers'}
          onCreated={() => setShowCreate(false)}
        />
      ),
    },
  ];

  return (
    <div>
      <div className="px-6 pt-6">
        <PageHeader
          title="TreatRx"
          actions={
            activeTab === 'providers' && !locked ? (
              <Button onClick={() => setShowCreate(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Add Provider
              </Button>
            ) : undefined
          }
        />
      </div>

      {/* Uncontrolled — Tabs defaults to tabs[0], which is the same id
          `activeTab` is seeded with, so the two never diverge. */}
      <Tabs
        tabs={tabs}
        onChange={(id) => {
          setActiveTab(id);
          setShowCreate(false);
        }}
      />
    </div>
  );
}
